import { useEffect, useMemo, useRef, useState } from 'react'

import styled from 'styled-components'

import { useStorySeen } from 'api/stories/index'
import { useStoryPollOptimistic } from 'api/stories/useStoryPollOptimistic/useStoryPollOptimistic'
import placeholderAvatar from 'assets/images/user_placeholder.png'
import StatsPopup from 'components/UI/StatsPopup/StatsPopup'
import {
    type IProgress,
    type PollAnswer,
    PostPoll,
    ProgressBar
} from 'components-ref/molecules'
import {
    StoryActions,
    StoryFooter,
    StoryHeader,
    StoryMedia,
    StoryViewersModal,
    ViewLikesModal
} from 'components-ref/organisms'
import { useGlobalVideo } from 'context/globalVideoContext'
import { useEventDefiner } from 'helpers/hooks'
import { type UserProfile } from 'models'
import {
  STILL_DURATION_MS,
  getStillElapsedMs
} from 'services/stories/storyStillClock/storyStillClock'
import { useUserProfile } from 'store'
import { type Story } from 'types/api/story'
import { type StoryActionType } from 'types/types'

enum Side {
    left = 'left',
    right = 'right'
}

interface IStoryFullscreenProps {
    story: Story
    user: UserProfile
    progressList: IProgress[]
    isPaused: boolean
    activeStoryIndex: number
    withCloseButton: boolean
    setIsPaused: (x: boolean) => any
    setAction: (action: StoryActionType) => any
    onBack?: () => any
    onForward?: () => any
    onSwipeLeft?: () => any
    onSwipeRight?: () => any
    preloadOnMount?: boolean
    onClose?: () => void
    userId?: string
    username?: string
    displayName?: string
    avatar?: string | null
    isInViewport?: boolean
    homeFeed?: boolean
}

export const StoryFullscreen = ({
    story,
    user,
    progressList,
    homeFeed = false,
    isPaused,
    activeStoryIndex,
    withCloseButton = true,
    setIsPaused,
    setAction,
    onBack,
    onForward,
    onSwipeLeft,
    onSwipeRight,
    userId,
    username,
    displayName,
    avatar,
    isInViewport = true,
}: IStoryFullscreenProps) => {
    const [isPlayerMuted, setIsPlayerMuted] = useState<boolean>(false)
    const [isViewsOpen, setIsViewsOpen] = useState<boolean>(false)
    const [isLikesOpen, setIsLikesOpen] = useState<boolean>(false)
    const [isStatisticsOpen, setIsStatisticsOpen] = useState<boolean>(false)
    const progressFillRef = useRef<HTMLDivElement | null>(null)
    const { getVideoElement } = useGlobalVideo()
    
    const suppressStatsRef = useRef(false)

    const loggedInUser = useUserProfile()

    const { storySeenMutation } = useStorySeen()


    // Photos and polls: paint progress from the shared 7-second clock.
    // This only updates the progress bar; it does not advance stories.
    useEffect(() => {
    if (!story || story.video_src) return

    const storyId = story.id
    let frameId = 0

    const paintProgress = () => {
        const elapsedMs = getStillElapsedMs(storyId)

        const fraction = Math.max(
        0,
        Math.min(1, elapsedMs / STILL_DURATION_MS)
        )

        if (progressFillRef.current) {
        progressFillRef.current.style.transform =
            `scaleX(${fraction})`
        }
    }

    // Restore the existing progress immediately when fullscreen opens.
    paintProgress()

    if (!isPaused && isInViewport) {
        const tick = () => {
        paintProgress()
        frameId = requestAnimationFrame(tick)
        }

        frameId = requestAnimationFrame(tick)
    }

        return () => {

        cancelAnimationFrame(frameId)
        }
    }, [
    story.id,
    story.video_src,
    activeStoryIndex,
    isPaused,
    isInViewport
    ])

    /*
     * storyPostId appears to be used by the optimistic story-poll helper
     * to identify the story inside the current story collection.
     *
     * Do not derive this from the route anymore. We already know exactly
     * which story is active here.
     */
    const { mutate: answerPoll } = useStoryPollOptimistic({
    story
    })

    const resolvedUserId = userId ?? user?.id

    const resolvedUsername =
        displayName ??
        username ??
        user?.username ??
        ''

    useEffect(() => {
    if (!story?.video_src || !isInViewport) return

    const mediaId = `${homeFeed ? 'story' : 'story-list'}:${story.id}`
    const video = getVideoElement(mediaId, story.video_src)

    if (!video) return

    let frameId: number | null = null
    let videoFrameId: number | null = null
    let running = false

    const updateProgress = () => {
        const duration = video.duration

        const progress =
        Number.isFinite(duration) && duration > 0
            ? Math.max(0, Math.min(1, video.currentTime / duration))
            : 0

        if (progressFillRef.current) {
        progressFillRef.current.style.transform = `scaleX(${progress})`
        }
    }

    const tick = () => {
        if (!running) return

        updateProgress()
        frameId = requestAnimationFrame(tick)
    }

    const videoTick = () => {
        if (!running) return

        updateProgress()
        videoFrameId = video.requestVideoFrameCallback(videoTick)
    }

    const start = () => {
        if (running) return

        running = true
        updateProgress()

        if ('requestVideoFrameCallback' in video) {
        videoFrameId = video.requestVideoFrameCallback(videoTick)
        } else {
        frameId = requestAnimationFrame(tick)
        }
    }

    const stop = () => {
        running = false

        if (frameId !== null) {
        cancelAnimationFrame(frameId)
        frameId = null
        }

        if (
        videoFrameId !== null &&
        'cancelVideoFrameCallback' in video
        ) {
        video.cancelVideoFrameCallback(videoFrameId)
        videoFrameId = null
        }

        updateProgress()
    }

    video.addEventListener('play', start)
    video.addEventListener('playing', start)
    video.addEventListener('pause', stop)
    video.addEventListener('ended', stop)
    video.addEventListener('seeked', updateProgress)
    video.addEventListener('timeupdate', updateProgress)

    updateProgress()

    if (!video.paused && !video.ended && !isPaused) {
        start()
    }

    return () => {
        stop()

        video.removeEventListener('play', start)
        video.removeEventListener('playing', start)
        video.removeEventListener('pause', stop)
        video.removeEventListener('ended', stop)
        video.removeEventListener('seeked', updateProgress)
        video.removeEventListener('timeupdate', updateProgress)
    }
    }, [
    story?.id,
    story?.video_src,
    activeStoryIndex,
    isPaused,
    isInViewport,
    homeFeed,
    getVideoElement
    ])

    const storyStats = useMemo(() => {
    const stats = (story as any)?.statistics

    if (!stats) return undefined

    return {
        overallRevenue: Number(stats.tips_sum ?? 0),
        tipsCount: Number(stats.tips_count ?? 0),
        tipsSum: Number(stats.tips_sum ?? 0),
        uniqueViews: Number(stats.unique_views ?? 0),
        views: stats.views ?? []
    }
    }, [story])


    function closeModal() {
        suppressStatsRef.current = true

        setIsViewsOpen(false)
        setIsLikesOpen(false)
        setIsStatisticsOpen(false)

        setAction('longPress')
        setIsPaused(false)

        setTimeout(() => {
            suppressStatsRef.current = false
        }, 150)
    }

    function openLikesModal() {
        setTimeout(() => {
            setAction(null)
            setIsPaused(true)
            setIsLikesOpen(true)
        }, 10)
    }

    function openViewsModal() {
        setTimeout(() => {
            setAction(null)
            setIsPaused(true)
            setIsViewsOpen(true)
        }, 10)
    }

    function openStatisticsModal() {
        if (suppressStatsRef.current) {
            return
        }

        setTimeout(() => {
            setAction(null)
            setIsPaused(true)
            setIsStatisticsOpen(true)
        }, 10)
    }

    function handlePollAnswer(answer: PollAnswer) {
        answerPoll(answer)

        /*
         * Keep the story from treating the poll click as navigation /
         * continuing a long-press gesture.
         */
        setAction?.('longPress')
        setIsPaused(false)
    }

    const handleStorySeen = () => {
        storySeenMutation.mutate({
            activeStoryIndex,
            story
        })
    }

    useEffect(() => {
        if (
            !story?.seen &&
            loggedInUser?.id !== resolvedUserId
        ) {
            handleStorySeen()
        }
    }, [
        activeStoryIndex,
        story?.seen,
        loggedInUser?.id,
        resolvedUserId
    ])

    const { handlers: storyHandlers } = useEventDefiner({
        onLongPressStart: () => {
            setAction?.(null)
            setIsPaused(true)
        },

        onLongPressEnd: () => {
            setAction?.('longPress')
            setIsPaused(false)
        },

        onSwipeLeft: () => {
            setAction?.('swipeLeft')
            setIsPaused(false)
            onSwipeLeft?.()
        },

        onSwiperight() {
            setAction?.('swipeRight')
            setIsPaused(false)
            onSwipeRight?.()
        }
    })

    const { handlers: leftHandlers } = useEventDefiner({
        onClick: () => {
            setAction?.('click')
            setIsPaused(false)
            onBack?.()
        }
    })

    const { handlers: rightHandlers } = useEventDefiner({
        onClick: () => {
            setAction?.('click')
            setIsPaused(false)
            onForward?.()
        }
    })


    return (
        <>
            {isStatisticsOpen && (
                <StatsBackdrop
                    onPointerDownCapture={e => {
                        e.preventDefault()
                        e.stopPropagation()

                        requestAnimationFrame(() => {
                            closeModal()
                        })
                    }}
                    onPointerUpCapture={e => {
                        e.preventDefault()
                        e.stopPropagation()
                    }}
                    onClickCapture={e => {
                        e.preventDefault()
                        e.stopPropagation()
                    }}
                >
                    <StatsCard
                        onPointerDownCapture={e => {
                            e.stopPropagation()
                        }}
                        onPointerUpCapture={e => {
                            e.stopPropagation()
                        }}
                        onClickCapture={e => {
                            e.stopPropagation()
                        }}
                    >
                    <StatsPopup
                    centered
                    statsData={storyStats}
                    closeStats={closeModal}
                    storyId={story.id}
                    />
                    </StatsCard>
                </StatsBackdrop>
            )}

            {isViewsOpen && (
                <StoryViewersModal
                    storyId={story.id}
                    onClose={closeModal}
                />
            )}

            {isLikesOpen && (
                <ViewLikesModal
                    onClose={closeModal}
                    postId={story.id}
                    type="story"
                />
            )}

            <StoryRoot
                {...(!isStatisticsOpen
                    ? storyHandlers
                    : {})}
            >
                <StoryMediaStack>
                <StoryMediaLayer key={story.id}>
                    <StoryMedia
                    story={story}
                    user={user}
                    homeFeed={homeFeed}
                    isPlayerMuted={isPlayerMuted}
                    isPaused={isPaused || !isInViewport}
                    isActive={isInViewport}
                    setIsPaused={setIsPaused}
                    setAction={setAction}
                    activeStoryIndex={activeStoryIndex}
                    />
                </StoryMediaLayer>
                </StoryMediaStack>

                <StoryOverlay>
                    <ChangeStoryHandler
                        {...leftHandlers}
                        $side={Side.left}
                    />

                    <ChangeStoryHandler
                        {...rightHandlers}
                        $side={Side.right}
                    />

                    <DesktopStoryNavButton
                        $side={Side.left}
                        onClick={e => {
                            e.stopPropagation()
                            onBack?.()
                        }}
                        aria-label="Previous story"
                    >
                        <span className="story-nav-icon">‹</span>
                    </DesktopStoryNavButton>

                    <DesktopStoryNavButton
                        $side={Side.right}
                        onClick={e => {
                            e.stopPropagation()
                            onForward?.()
                        }}
                        aria-label="Next story"
                    >
                        <span className="story-nav-icon">›</span>
    </DesktopStoryNavButton>

                    {story?.poll && (
                        <StoryPollLayer
                            onClick={e => {
                                e.stopPropagation()
                            }}
                            onMouseDown={e => {
                                e.stopPropagation()
                            }}
                            onMouseUp={e => {
                                e.stopPropagation()
                            }}
                            onTouchStart={e => {
                                e.stopPropagation()
                            }}
                            onTouchEnd={e => {
                                e.stopPropagation()
                            }}
                            onPointerDown={e => {
                                e.stopPropagation()
                            }}
                            onPointerUp={e => {
                                e.stopPropagation()
                            }}
                        >
                            <PostPoll
                                pollData={story.poll}
                                setAnswer={handlePollAnswer}
                                isMyPost={
                                    Boolean(
                                        story.user_id &&
                                        loggedInUser?.id &&
                                        story.user_id ===
                                            loggedInUser.id
                                    )
                                }
                            />
                        </StoryPollLayer>
                    )}

                    <ProgressBar
                    key={activeStoryIndex}
                    progressList={progressList}
                    activeIndex={activeStoryIndex}
                    activeFillRef={progressFillRef}
                    isPlaying={true}
                    hasStartedStory={true}
                    allStoriesSeen={false}
                    />

                    <StoryHeader
                        avatar={
                            avatar ??
                            user?.avatar?.url ??
                            placeholderAvatar
                        }
                        username={resolvedUsername}
                        id={resolvedUserId}
                        date={new Date(story.created_at)}
                        withCloseButton={withCloseButton}
                        storyId={story.id}
                    />

                    <StoryActions
                        story={story}
                        setIsPaused={setIsPaused}
                        setAction={setAction}
                        openLikesModal={openLikesModal}
                        openStatisticsModal={
                            openStatisticsModal
                        }
                        closeModal={closeModal}
                        openViewsModal={openViewsModal}
                    />

                    <StoryFooter
                        avatarsList={
                            story.statistics
                                ? story.statistics.views
                                : []
                        }
                        storyUserId={resolvedUserId}
                        storyId={story.id}
                        setAction={setAction}
                        setIsPaused={setIsPaused}
                        isPlayerMuted={isPlayerMuted}
                        setIsPlayerMuted={
                            setIsPlayerMuted
                        }
                        openViewsModal={openViewsModal}
                    />
                </StoryOverlay>
            </StoryRoot>
        </>
    )
}

const StoryRoot = styled.div`
    position: fixed;
    inset: 0;

    width: 100vw;
    height: 100vh;

    overflow: hidden;
    background: black;
`

const StoryMediaStack = styled.div`
    position: fixed;
    inset: 0;

    z-index: 1;

    overflow: hidden;
    background: black;
`

const StoryOverlay = styled.div`
    position: fixed;
    inset: 0;

    z-index: 5;

    pointer-events: none;

    > * {
        pointer-events: auto;
    }
`

const ChangeStoryHandler = styled.div<{
    $side: Side
}>`
    position: absolute;
    top: 0;

    left: ${props =>
        props.$side === Side.left
            ? '0'
            : '50%'};

    width: 50%;
    height: 100%;

    z-index: 4;
`

const StoryPollLayer = styled.div`
    position: absolute;

    left: 50%;
    bottom: 10rem;

    transform: translateX(-50%);

    width: 100%;
    padding-bottom: 2rem;

    display: flex;
    justify-content: center;
    align-items: flex-end;

    /*
     * Must be higher than ChangeStoryHandler (4).
     *
     * This now works because they're inside the SAME StoryOverlay
     * stacking context.
     */
    z-index: 10;

    pointer-events: auto;
`
const StoryMediaLayer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  z-index: 3;

  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  contain: paint;
`
const DesktopStoryNavButton = styled.button<{
    $side: Side
}>`
    display: none;

    @media (min-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: center;

        position: fixed;
        top: 50%;
        transform: translateY(-50%);

        width: 5.5rem;
        height: 5.5rem;

        border: none;
        border-radius: 50%;

        background: rgba(15, 15, 19, 0.5);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);

        color: white;
        font-size: 4rem;
        line-height: 1;
        font-weight: 300;

        z-index: 20;
        cursor: pointer;

        transition:
            transform 0.15s ease,
            background 0.15s ease;

        ${
            props =>
                props.$side === Side.left
                    ? `
                        left: max(
                            3rem,
                            calc(50vw - 50vh - 3rem)
                        );
                    `
                    : `
                        right: max(
                            3rem,
                            calc(50vw - 50vh - 3rem)
                        );
                    `
        }

        &:hover {
            background: rgba(15, 15, 19, 0.7);
            transform: translateY(-50%) scale(1.08);
        }
.story-nav-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    line-height: 1;
    position: relative;
    top: -0.35rem;
}
        &:active {
            transform: translateY(-50%) scale(0.96);
        }
    }
`
const StatsBackdrop = styled.div`
    position: fixed;
    inset: 0;

    z-index: 999999;

    background: rgba(0, 0, 0, 0.4);

    display: flex;
    align-items: center;
    justify-content: center;

    pointer-events: auto;
`

const StatsCard = styled.div`
    pointer-events: auto;

    .stats__overlay {
        position: relative !important;

        top: auto !important;
        right: auto !important;
        left: auto !important;
        bottom: auto !important;
    }
`